import { supabase } from '../lib/supabase';

export interface OnboardingCall {
  id: string;
  partner_id: string;
  business_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  selected_time_slot: string;
  onboarding_call_completed: boolean;
  onboarding_call_notes: string | null;
  calendar_slot_selected: boolean;
  status: string;
  call_type: 'introduction' | 'system_training' | 'technical_setup';
}

export interface CallQueueItem {
  id: string;
  business_name: string;
  contact_name: string;
  selected_time_slot: string;
  status: string;
  call_type: string;
  preparation_checklist: string[];
}

export interface ScheduleBlock {
  id: string;
  blocked_date: string;
  start_time: string;
  end_time: string;
  reason: string | null;
  created_at: string;
  is_auto_generated?: boolean; // Add field to distinguish automated blocks
}

export interface ConsolidatedScheduleBlock {
  id: string; // ID of the first block in the consolidated group
  ids: string[]; // All IDs in the consolidated group
  blocked_date: string;
  start_time: string;
  end_time: string;
  reason: string | null;
  created_at: string;
  hours_count: number; // Number of consecutive hours
}

export const onboardingCallsService = {
  /**
   * Fetch all scheduled onboarding calls
   * Retrieves public calendar bookings from calendar_bookings table
   */
  async getScheduledCalls(): Promise<OnboardingCall[]> {
    try {
      const { data, error } = await supabase
        .from('calendar_bookings')
        .select('*')
        .order('selected_time_slot', { ascending: true });

      if (error) throw error;

      return (data || []).map(booking => ({
        id: booking.id,
        partner_id: booking.id,
        business_name: booking.business_name,
        contact_name: booking.contact_name,
        contact_email: booking.contact_email,
        contact_phone: booking.contact_phone || 'N/A',
        selected_time_slot: booking.selected_time_slot,
        onboarding_call_completed: false,
        onboarding_call_notes: booking.booking_notes,
        calendar_slot_selected: true,
        status: 'scheduled',
        call_type: 'introduction'
      }));
    } catch (error) {
      console.error('Error fetching scheduled calls:', error);
      throw error;
    }
  },

  /**
   * Get upcoming appointments (next 7 days)
   */
  async getUpcomingAppointments(): Promise<OnboardingCall[]> {
    try {
      const now = new Date();
      const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .eq('calendar_slot_selected', true)
        .not('selected_time_slot', 'is', null)
        .gte('selected_time_slot', now.toISOString())
        .lte('selected_time_slot', nextWeek.toISOString())
        .order('selected_time_slot', { ascending: true });

      if (error) throw error;

      return (data || []).map(partner => ({
        id: partner.id,
        partner_id: partner.id,
        business_name: partner.business_name,
        contact_name: partner.contact_name || 'N/A',
        contact_email: partner.contact_email,
        contact_phone: partner.contact_phone || 'N/A',
        selected_time_slot: partner.selected_time_slot,
        onboarding_call_completed: partner.onboarding_call_completed || false,
        onboarding_call_notes: partner.onboarding_call_notes,
        calendar_slot_selected: partner.calendar_slot_selected,
        status: partner.status,
        call_type: 'introduction'
      }));
    } catch (error) {
      console.error('Error fetching upcoming appointments:', error);
      throw error;
    }
  },

  /**
   * Get call history (completed calls)
   */
  async getCallHistory(): Promise<OnboardingCall[]> {
    try {
      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .eq('onboarding_call_completed', true)
        .order('onboarding_completed_at', { ascending: false });

      if (error) throw error;

      return (data || []).map(partner => ({
        id: partner.id,
        partner_id: partner.id,
        business_name: partner.business_name,
        contact_name: partner.contact_name || 'N/A',
        contact_email: partner.contact_email,
        contact_phone: partner.contact_phone || 'N/A',
        selected_time_slot: partner.selected_time_slot || '',
        onboarding_call_completed: true,
        onboarding_call_notes: partner.onboarding_call_notes,
        calendar_slot_selected: partner.calendar_slot_selected,
        status: partner.status,
        call_type: 'system_training'
      }));
    } catch (error) {
      console.error('Error fetching call history:', error);
      throw error;
    }
  },

  /**
   * Complete onboarding call and add notes
   */
  async completeCall(
    partnerId: string,
    notes: string,
    followUpActions: string[]
  ): Promise<void> {
    try {
      const { error } = await supabase.rpc('complete_onboarding_call', {
        p_partner_id: partnerId,
        p_notes: notes
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error completing call:', error);
      throw error;
    }
  },

  /**
   * Update call notes
   */
  async updateCallNotes(partnerId: string, notes: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('partners')
        .update({ 
          onboarding_call_notes: notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', partnerId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating call notes:', error);
      throw error;
    }
  },

  /**
   * Get partner onboarding progress
   */
  async getOnboardingProgress(partnerId: string): Promise<{
    milestones: Array<{ name: string; completed: boolean; date?: string }>;
    nextAction: string;
  }> {
    try {
      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .eq('id', partnerId)
        .single();

      if (error) throw error;

      const milestones = [
        {
          name: 'Calendar Slot Selected',
          completed: data?.calendar_slot_selected || false,
          date: data?.calendar_link_sent_at
        },
        {
          name: 'Onboarding Call Completed',
          completed: data?.onboarding_call_completed || false,
          date: data?.onboarding_completed_at
        },
        {
          name: 'Contract Sent',
          completed: !!data?.contract_sent_at,
          date: data?.contract_sent_at
        },
        {
          name: 'Contract Signed',
          completed: data?.contract_signed || false,
          date: data?.contract_signed_at
        }
      ];

      let nextAction = 'Schedule onboarding call';
      if (data?.calendar_slot_selected && !data?.onboarding_call_completed) {
        nextAction = 'Complete onboarding call';
      } else if (data?.onboarding_call_completed && !data?.contract_sent_at) {
        nextAction = 'Send contract';
      } else if (data?.contract_sent_at && !data?.contract_signed) {
        nextAction = 'Follow up on contract signing';
      } else if (data?.contract_signed) {
        nextAction = 'Account activation';
      }

      return { milestones, nextAction };
    } catch (error) {
      console.error('Error fetching onboarding progress:', error);
      throw error;
    }
  },

  /**
   * Delete a calendar booking (admin only)
   */
  async deleteCalendarBooking(bookingId: string): Promise<void> {
    try {
      const { data, error } = await supabase.rpc('delete_calendar_booking', {
        booking_uuid: bookingId
      });

      if (error) throw error;

      // Extract success result
      let success = false;
      let message = 'Operation completed';

      if (data) {
        if (Array.isArray(data) && data.length > 0) {
          success = data[0].success || false;
          message = data[0].message || message;
        } else if (typeof data === 'object') {
          success = data.success || false;
          message = data.message || message;
        }
      }

      if (!success) {
        throw new Error(message);
      }
    } catch (error) {
      console.error('Error deleting calendar booking:', error);
      throw error;
    }
  },

  /**
   * Create a schedule block (admin only)
   */
  async createScheduleBlock(
    blockedDate: string,
    startTime: string,
    endTime: string,
    reason?: string
  ): Promise<string> {
    try {
      const { data, error } = await supabase.rpc('create_schedule_block', {
        p_blocked_date: blockedDate,
        p_start_time: startTime,
        p_end_time: endTime,
        p_reason: reason || null
      });

      if (error) throw error;

      // Extract success result
      let success = false;
      let message = 'Operation completed';
      let blockId = null;

      if (data) {
        if (Array.isArray(data) && data.length > 0) {
          success = data[0].success || false;
          message = data[0].message || message;
          blockId = data[0].block_id;
        } else if (typeof data === 'object') {
          success = data.success || false;
          message = data.message || message;
          blockId = data.block_id;
        }
      }

      if (!success) {
        throw new Error(message);
      }

      return blockId;
    } catch (error) {
      console.error('Error creating schedule block:', error);
      throw error;
    }
  },

  /**
   * Get all schedule blocks
   */
  async getScheduleBlocks(startDate?: string, endDate?: string): Promise<ScheduleBlock[]> {
    try {
      // Query the table directly instead of using RPC function to avoid function not found errors
      let query = supabase
        .from('admin_schedule_blocks')
        .select('id, blocked_date, start_time, end_time, reason, created_at, created_by')
        .order('blocked_date', { ascending: true })
        .order('start_time', { ascending: true });

      // Apply date filters if provided
      if (startDate) {
        query = query.gte('blocked_date', startDate);
      }
      if (endDate) {
        query = query.lte('blocked_date', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || []).map((block: any) => ({
        id: block.id,
        blocked_date: block.blocked_date,
        start_time: block.start_time,
        end_time: block.end_time,
        reason: block.reason,
        created_at: block.created_at,
        // Only mark as auto-generated if created_by is null (automated by trigger)
        is_auto_generated: block.created_by === null
      }));
    } catch (error) {
      console.error('Error fetching schedule blocks:', error);
      throw error;
    }
  },

  /**
   * Get consolidated manual schedule blocks (combines consecutive hours)
   */
  async getConsolidatedManualBlocks(startDate?: string, endDate?: string): Promise<ConsolidatedScheduleBlock[]> {
    try {
      const allBlocks = await this.getScheduleBlocks(startDate, endDate);
      
      // Filter to only manual blocks (created by admins)
      const manualBlocks = allBlocks.filter(block => !block.is_auto_generated);
      
      if (manualBlocks.length === 0) return [];
      
      // Group blocks by date
      const blocksByDate: { [date: string]: ScheduleBlock[] } = {};
      manualBlocks.forEach(block => {
        if (!blocksByDate[block.blocked_date]) {
          blocksByDate[block.blocked_date] = [];
        }
        blocksByDate[block.blocked_date].push(block);
      });
      
      const consolidatedBlocks: ConsolidatedScheduleBlock[] = [];
      
      // For each date, consolidate consecutive blocks
      Object.keys(blocksByDate).forEach(date => {
        const dateBlocks = blocksByDate[date];
        
        // Sort by start time
        dateBlocks.sort((a, b) => {
          const aHour = parseInt(a.start_time.split(':')[0]);
          const bHour = parseInt(b.start_time.split(':')[0]);
          return aHour - bHour;
        });
        
        let currentGroup: ScheduleBlock[] = [];
        
        dateBlocks.forEach((block, index) => {
          if (currentGroup.length === 0) {
            currentGroup.push(block);
            return;
          }
          
          const lastBlock = currentGroup[currentGroup.length - 1];
          const lastEndHour = parseInt(lastBlock.end_time.split(':')[0]);
          const currentStartHour = parseInt(block.start_time.split(':')[0]);
          
          // Check if this block is consecutive (starts when last one ends)
          if (currentStartHour === lastEndHour) {
            currentGroup.push(block);
          } else {
            // Save current group and start new one
            consolidatedBlocks.push(this.createConsolidatedBlock(currentGroup));
            currentGroup = [block];
          }
          
          // If this is the last block, save the current group
          if (index === dateBlocks.length - 1) {
            consolidatedBlocks.push(this.createConsolidatedBlock(currentGroup));
          }
        });
        
        // Handle case where we didn't push the last group yet
        if (currentGroup.length > 0 && 
            !consolidatedBlocks.find(cb => cb.ids.includes(currentGroup[0].id))) {
          consolidatedBlocks.push(this.createConsolidatedBlock(currentGroup));
        }
      });
      
      return consolidatedBlocks;
    } catch (error) {
      console.error('Error fetching consolidated manual blocks:', error);
      throw error;
    }
  },

  /**
   * Helper to create a consolidated block from a group of consecutive blocks
   */
  createConsolidatedBlock(blocks: ScheduleBlock[]): ConsolidatedScheduleBlock {
    const firstBlock = blocks[0];
    const lastBlock = blocks[blocks.length - 1];
    
    return {
      id: firstBlock.id,
      ids: blocks.map(b => b.id),
      blocked_date: firstBlock.blocked_date,
      start_time: firstBlock.start_time,
      end_time: lastBlock.end_time,
      reason: firstBlock.reason,
      created_at: firstBlock.created_at,
      hours_count: blocks.length
    };
  },

  /**
   * Delete a schedule block (admin only)
   */
  async deleteScheduleBlock(blockId: string): Promise<void> {
    try {
      const { data, error } = await supabase.rpc('delete_schedule_block', {
        p_block_id: blockId
      });

      if (error) throw error;

      // Extract success result
      let success = false;
      let message = 'Operation completed';

      if (data) {
        if (Array.isArray(data) && data.length > 0) {
          success = data[0].success || false;
          message = data[0].message || message;
        } else if (typeof data === 'object') {
          success = data.success || false;
          message = data.message || message;
        }
      }

      if (!success) {
        throw new Error(message);
      }
    } catch (error) {
      console.error('Error deleting schedule block:', error);
      throw error;
    }
  },

  /**
   * Check if a time slot is blocked
   */
  async isTimeSlotBlocked(slotTimestamp: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('is_time_slot_blocked', {
        slot_timestamp: slotTimestamp
      });

      if (error) throw error;

      return data || false;
    } catch (error) {
      console.error('Error checking if time slot is blocked:', error);
      return false;
    }
  }
};